/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * ClipBasketV2.jsx — Upload Basket v2.
 *
 * The intake layer (MobilePhotosIntake) is the source of truth for the
 * picker → raw-File → staging-persistence boundary. This component
 * is only responsible for:
 *   • Showing the staged rows already persisted to the local draft
 *   • Letting the user start chunked uploads (after explicit consent)
 *   • Letting the user start analysis once every row is uploaded
 *
 * Doctrine (operator-locked May 21, 2026):
 *   1. Selection is not upload consent.
 *   2. Local draft is authoritative the instant onChange fires.
 *   3. No metadata / duration / thumbnail / upload prep before the
 *      raw files are persisted in the draft.
 *   4. AI cannot run until every row is `uploaded`.
 *   5. The basket NEVER returns to 0 clips after files have been
 *      selected — the draft survives the modal unmounting.
 *
 * No legacy picker hooks. No shared picker bus in the intake path.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Loader2, AlertCircle, CheckCircle2, Trash2, Brain, AlertTriangle } from "lucide-react";
import { chunkedUpload, AuthExpiredError, UploadPausedError, DiskExhaustedError } from "../../lib/chunkedUpload";
import { cloudUpload, CloudConfigError } from "../../lib/cloudUpload";
import { attemptResume } from "../../lib/uploadRecovery";
import {
  emptyDraft, loadDraft, saveDraft, clearDraft,
  mergeBackendHydration, stageRawFilesSync,
} from "../../lib/clipBasketDraft";
import MobilePhotosIntake from "./MobilePhotosIntake";
import { intakeLog } from "../../lib/intakeLog";
import { useOperationalLog } from "../../context/OperationalStateContext";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;
const INTENT_OPTIONS = [
  { id: "learner_self_drive",  label: "Learner self-drive lesson" },
  { id: "instructor_coaching", label: "Instructor coaching review" },
  { id: "pdi_part3",           label: "PDI Part 3 practice" },
  { id: "independent_drive",   label: "Independent drive" },
  { id: "mock_test",           label: "Mock test" },
  { id: "general_road_footage", label: "General road footage" },
];

export default function ClipBasketV2({ user, onClose, onHandedOff, initialIntent = null }) {
  const uid = user?._id || user?.id || user?.email || "anon";
  const { appendEvent } = useOperationalLog();
  const [draft, setDraft] = useState(() => loadDraft(uid) || emptyDraft({ intent: initialIntent }));
  const [pickerStuck, setPickerStuck] = useState(false);
  // Last failed analyse-handoff message. Cleared on a fresh attempt.
  // Drives the inline retry banner so the user has a visible
  // recoverable surface instead of relying on a fading toast.
  const [analyseError, setAnalyseError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const intakeRef = useRef(null);
  const draftRef = useRef(draft);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Persist UI-driven mutations (intent picks, row removals, status
  // transitions). The intake hot path persists synchronously via
  // stageRawFilesSync — this effect handles every OTHER state change.
  useEffect(() => { saveDraft(uid, draft); }, [uid, draft]);

  // Recovery probe on mount — for every persisted row that already
  // has an upload_id (i.e. user is returning after refresh / kill /
  // background), ask the recovery authority whether the server-side
  // session is still resumable. Result drives row status: complete →
  // mark uploaded; expired → mark failed with a named reason;
  // resumable → leave row staged with a server-confirmed resume index.
  //
  // Phase B scope: probe + status update only. The actual resumed
  // upload runs through chunkedUpload({ resumeUploadId }) when the
  // operator taps Start (or Retry). See PLATFORM_REBUILD §10 Phase B.
  const recoveryProbedRef = useRef(false);
  useEffect(() => {
    if (recoveryProbedRef.current) return;
    const rowsWithUploadId = draftRef.current.rows.filter(
      (r) => r.uploadId && r.uploadPath === "chunked"
        && (r.status === "local_staged" || r.status === "uploading" || r.status === "failed"),
    );
    if (rowsWithUploadId.length === 0) return;
    recoveryProbedRef.current = true;
    let cancelled = false;
    (async () => {
      for (const row of rowsWithUploadId) {
        const result = await attemptResume({ upload_id: row.uploadId });
        if (cancelled) return;
        intakeLog("v2_recovery_probe", {
          row_id: row.rowId, upload_id: row.uploadId,
          resumable: result.resumable, reason: result.reason || null,
          last_chunk_index: result.last_chunk_index ?? null,
        });
        try {
          appendEvent("upload_resume_attempted", {
            source: "mount_probe",
            row_id: row.rowId,
            upload_id: row.uploadId,
            basket_id: draftRef.current.backendBasketId || null,
            endpoint_url: `/api/uploads/chunked/${row.uploadId}/status`,
            method: "GET",
            resumable: result.resumable,
            reason: result.reason || null,
            http_status: result.http_status ?? null,
            cloudflare_shaped: result.cloudflare_shaped ?? null,
            last_chunk_index: result.last_chunk_index ?? null,
            next_chunk_index: result.next_chunk_index ?? null,
            total_chunks: result.total_chunks ?? null,
            failure_phase: result.resumable ? null : "during_resume_probe",
          });
        } catch { /* observability never crashes */ }
        if (result.complete) {
          setDraft((d) => ({
            ...d,
            rows: d.rows.map((r) => r.rowId === row.rowId
              ? { ...r, status: "uploaded", progressPct: 100, error: null }
              : r),
          }));
        } else if (result.reason === "session_expired") {
          setDraft((d) => ({
            ...d,
            rows: d.rows.map((r) => r.rowId === row.rowId
              ? { ...r, status: "failed", error: "Upload session expired — re-pick this clip to retry.", uploadId: null }
              : r),
          }));
        } else if (result.resumable) {
          // Stamp the server-confirmed resume point onto the row so
          // the operator sees "Resuming from chunk N" before tapping
          // Start. The actual resume is driven by uploadRow below.
          setDraft((d) => ({
            ...d,
            rows: d.rows.map((r) => r.rowId === row.rowId
              ? { ...r, resumeFromChunk: result.next_chunk_index, totalChunks: result.total_chunks || r.totalChunks }
              : r),
          }));
        }
        // session_unknown / auth_expired / network_unreachable: leave
        // the row as-is; user action (Start / Retry) will surface the
        // real failure.
      }
    })();
    return () => { cancelled = true; };
  }, []);   // intentional one-shot on mount

  // Backend session catch-up — happens AFTER rows exist locally.
  useEffect(() => {
    if (!draft.rows.length || draft.backendBasketId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/api/clip-basket/create`,
          { analysis_intent: draft.intent || null, schema_version: 2, client_draft_id: draft.draftId },
          { withCredentials: true });
        if (cancelled) return;
        const backendId = r?.data?.id;
        if (backendId) {
          setDraft((d) => mergeBackendHydration(d, backendId));
          intakeLog("v2_backend_session_created", { basket_id: backendId, local_rows: draftRef.current.rows.length });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[v2] backend session create failed", err);
        intakeLog("v2_backend_session_failed", { error: String(err?.message || err) });
      }
    })();
    return () => { cancelled = true; };
  }, [draft.rows.length, draft.backendBasketId, draft.intent, draft.draftId]);

  // ── INTAKE HOT PATH ─────────────────────────────────────────────
  // Synchronously persist raw File handles into the durable draft
  // BEFORE React re-renders. No metadata work here.
  const handleFilesStaged = useCallback((files) => {
    setPickerStuck(false);
    const { draft: next, added, reattached } = stageRawFilesSync(uid, files, draftRef.current);
    intakeLog("v2_rows_persisted", { added, reattached, total: next.rows.length });
    // Operator-visible diagnostic. One event per staged file with
    // full fingerprint so the copy/export bundle contains an
    // unambiguous record of "what the user picked".
    try {
      for (const f of files || []) {
        appendEvent("basket_row_staged", {
          source: "pick",
          file_name: f?.name || null,
          file_size: f?.size || 0,
          file_type: f?.type || null,
          file_last_modified: f?.lastModified || null,
          rows_added: added,
          rows_reattached: reattached,
          basket_id: draftRef.current.backendBasketId || null,
        });
      }
    } catch { /* observability never crashes */ }
    setDraft(next);
  }, [uid, appendEvent]);

  const handlePickerStuck = useCallback(() => {
    // Watchdog: 60 s elapsed since .click() and no onChange fired.
    // The user is told visibly, not silently ghosted.
    intakeLog("v2_picker_watchdog_surfaced");
    setPickerStuck(true);
  }, []);

  const openPicker = useCallback(() => {
    // Clear any previous "stuck" callout when the user retries.
    setPickerStuck(false);
    intakeLog("v2_add_videos_clicked", { has_intent: !!draftRef.current.intent });
    intakeRef.current?.openPicker();
  }, []);

  const removeRow = useCallback((rowId) => {
    setDraft((prev) => ({ ...prev, rows: prev.rows.filter((r) => r.rowId !== rowId) }));
  }, []);

  const uploadRow = useCallback(async (rowId) => {
    const row = draftRef.current.rows.find((r) => r.rowId === rowId);
    if (!row || !row.file) { toast.error("This clip needs to be re-picked first."); return; }
    // P0 May 22, 2026 — size-0 guard at upload time. The row was
    // staged immediately for UX continuity even if iCloud hadn't
    // finished downloading; here we re-check and surface a clear
    // recovery path before chunked upload would burn cycles on an
    // empty file.
    if (!row.file.size) {
      const msg = "This clip is still preparing in Photos. Open it in Photos so iCloud finishes downloading, then tap Retry.";
      setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, status: "failed", error: msg } : r) }));
      intakeLog("v2_upload_blocked_size_zero", { row_id: rowId });
      return;
    }
    if (!draftRef.current.backendBasketId) { toast.error("Still connecting to the server. Try again in a moment."); return; }
    intakeLog("v2_upload_started", { row_id: rowId, basket_id: draftRef.current.backendBasketId });
    // Operator-visible diagnostic for the row entering upload. The
    // canonical event is upload_init_requested; chunkedUpload will
    // also emit init/acked/chunk events below.
    try {
      appendEvent("upload_init_requested", {
        source: row.uploadId ? "row_resume" : "row_fresh",
        row_id: rowId,
        upload_id_before: row.uploadId || null,
        basket_id: draftRef.current.backendBasketId,
        file_name: row.file?.name || null,
        file_size: row.file?.size || 0,
        file_type: row.file?.type || null,
        resume_used: Boolean(row.uploadId),
        resume_from_chunk: row.resumeFromChunk ?? null,
        total_chunks_hint: row.totalChunks ?? null,
      });
    } catch { /* observability never crashes */ }
    setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, status: "uploading", error: null } : r) }));
    const basketId = draftRef.current.backendBasketId;
    // R2-native upload path (May 24, 2026 — Pass 1).
    // Routing decision is by `uploadPath` tag, NOT by uploadId presence.
    // Reason: stale chunked uploadIds from prior failed attempts
    // (persisted in localStorage) used to drag every retry back to
    // the chunked path forever. The tag is authoritative:
    //   • `cloud`   → cloudUpload (R2 multipart, can resume via
    //                  cloudUpload's own upload_id)
    //   • `chunked` → chunkedUpload (legacy resume of in-flight
    //                  chunks on the host filesystem)
    //   • unset     → fresh row → cloud (the operator-locked default)
    const onCommonProgress = ({ pct, upload_id }) => {
      setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, progressPct: pct, uploadId: upload_id || r.uploadId } : r) }));
    };
    const lifecycleFwd = (name, payload) => {
      try { appendEvent(name, { ...payload, row_id: rowId, basket_id: basketId }); }
      catch { /* observability never crashes */ }
    };
    // Forensic branch logging (May 24, 2026 — Pass 1 verification).
    // Proves which upload authority received this row. Read in the
    // diagnostic feed + browser console on the next iPhone test.
    const _branchTaken = (row.uploadPath === "chunked" && row.uploadId) ? "chunked" : "cloud";
    try {
      intakeLog("v2_upload_branch_selected", {
        row_id: rowId,
        upload_path: row.uploadPath ?? null,
        upload_id: row.uploadId ?? null,
        resume_from_chunk: row.resumeFromChunk ?? null,
        branch_taken: _branchTaken,
        reason: _branchTaken === "chunked"
          ? "row has uploadPath=chunked AND uploadId (legacy resume honoured)"
          : "fresh row or no chunked tag → R2-native cloud path",
      });
    } catch { /* observability never crashes */ }
    // eslint-disable-next-line no-console
    try { console.info("[ClipBasketV2] upload branch", { rowId, uploadPath: row.uploadPath, uploadId: row.uploadId, branch_taken: _branchTaken }); } catch { /* noop */ }
    try {
      if (row.uploadPath === "chunked" && row.uploadId) {
        // Resume-in-progress of a row that originated on the chunked
        // path BEFORE Pass 1 landed. Honour its existing server-side
        // chunks rather than orphaning them.
        await chunkedUpload({
          file: row.file,
          purpose: "clip_basket",
          completeUrl: `/api/clip-basket/${basketId}/chunked-complete`,
          completeBody: {},
          context: { basket_id: basketId, client_row_id: rowId },
          resumeUploadId: row.uploadId,
          onLifecycleEvent: lifecycleFwd,
          onProgress: onCommonProgress,
        });
      } else {
        // Fresh row OR a row whose previous chunked attempt has been
        // discarded (no `uploadPath === "chunked"` tag). Use R2.
        // Tag the row up-front so a mid-flight retry routes back to
        // cloud rather than falling through to chunked.
        setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, uploadPath: "cloud", uploadId: null } : r) }));
        await cloudUpload({
          file: row.file,
          purpose: "clip_basket",
          completeUrl: `/api/clip-basket/${basketId}/cloud-complete`,
          completeBody: {
            filename: row.file?.name,
            mime_type: row.file?.type || null,
          },
          context: { basket_id: basketId, client_row_id: rowId },
          onProgress: onCommonProgress,
        });
      }
      setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, status: "uploaded", progressPct: 100, error: null } : r) }));
      intakeLog("v2_upload_completed", { row_id: rowId });
    } catch (err) {
      if (err instanceof AuthExpiredError) { toast.error("You're signed out — please log in again."); return; }
      // Cloud upload is not configured on the host — terminal,
      // operator-action-required. Stop with a clear message; do NOT
      // fall back to the local chunked-relay path.
      if (err instanceof CloudConfigError) {
        const reason = "Cloud upload is not configured in preview";
        setDraft((d) => ({ ...d, rows: d.rows.map((r) =>
          r.rowId === rowId
            ? { ...r, status: "failed", error: reason, uploadId: null, resumeFromChunk: null }
            : r
        ) }));
        toast.error(reason, { duration: 8000 });
        intakeLog("v2_upload_failed", { row_id: rowId, reason: "cloud_not_configured" });
        return;
      }
      // Host filesystem ran out of space — terminal, non-retryable.
      // Surface a clear preview-specific message and DO NOT keep
      // resume-retrying (the disk won't grow back).
      if (err instanceof DiskExhaustedError) {
        const reason = err.serverMessage
          || "Preview storage is full — upload cannot continue here. Try production or use a shorter clip.";
        setDraft((d) => ({ ...d, rows: d.rows.map((r) =>
          r.rowId === rowId
            ? { ...r, status: "failed", error: reason, uploadId: null, resumeFromChunk: null }
            : r
        ) }));
        toast.error(reason, { duration: 8000 });
        intakeLog("v2_upload_failed", {
          row_id: rowId,
          reason: "host_storage_exhausted",
          stage: err.stage,
          available_bytes: err.availableBytes,
          required_bytes: err.requiredBytes,
        });
        return;
      }
      const reason = err instanceof UploadPausedError ? "Upload paused — tap retry." : (err?.message || "Upload failed.");
      setDraft((d) => ({ ...d, rows: d.rows.map((r) => r.rowId === rowId ? { ...r, status: "failed", error: reason } : r) }));
      intakeLog("v2_upload_failed", { row_id: rowId, reason });
    }
  }, [appendEvent]);

  const startAllUploads = useCallback(async () => {
    const staged = draftRef.current.rows.filter((r) => r.status === "local_staged");
    for (const r of staged) { // eslint-disable-next-line no-await-in-loop
      await uploadRow(r.rowId);
    }
  }, [uploadRow]);

  const analyseBasket = useCallback(async () => {
    const rows = draftRef.current.rows;
    if (!rows.length || rows.some((r) => r.status !== "uploaded")) { toast.error("Wait for every clip to finish uploading first."); return; }
    if (!draftRef.current.backendBasketId) { toast.error("Server session missing — try refreshing."); return; }
    const basketId = draftRef.current.backendBasketId;
    // Clear any prior error so the retry banner disappears while the
    // attempt is in flight.
    setAnalyseError(null);
    setVerifying(true);
    try {
      const r = await axios.post(
        `${API}/api/clip-basket/${basketId}/process`,
        { analysis_intent: draftRef.current.intent || "general_road_footage" },
        { withCredentials: true });
      intakeLog("v2_review_created", { review_id: r?.data?.lesson_review_id || null });
      try {
        appendEvent("basket_processed", {
          basket_id: basketId,
          review_id: r?.data?.lesson_review_id || null,
          clip_count: draftRef.current.rows.length,
          intent: draftRef.current.intent || null,
        });
      } catch { /* observability never crashes */ }

      // ── Pre-flight poll (May 24, 2026 P0).
      // The /process call returns 200 the instant the basket has
      // been validated for STAGED state (no missing chunks, every
      // clip has a storage source). Actual ffprobe validation +
      // ffmpeg concat happens in a background task. If a clip is
      // corrupted (e.g. random-byte synthetic test file, or an MP4
      // missing its moov atom) `_concat_and_handoff` reverts the
      // basket to `clips_staged` with `fallback_used` and an
      // `invalid_clips` array. Poll for up to 15 s so we can catch
      // this fast-fail and show a clean, persistent error banner
      // — instead of handing off to a screen where the failure is
      // invisible until the user goes hunting for it.
      let fastFail = null;
      for (let attempt = 0; attempt < 15; attempt++) {
        // Wait 1s between probes; first probe runs at t+1s.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 1000));
        let basket;
        try {
          // eslint-disable-next-line no-await-in-loop
          const probe = await axios.get(
            `${API}/api/clip-basket/${basketId}`,
            { withCredentials: true });
          basket = probe.data;
        } catch {
          // Network blip — keep polling, never block handoff on this.
          continue;
        }
        const status = basket?.status;
        const fallback = basket?.fallback_used;
        // Validation-fail surfaces — show specific filenames so the
        // user knows which clip to retry/discard.
        if (status === "clips_staged" && fallback &&
            fallback !== "instructor_full_merge_override") {
          const invalid = Array.isArray(basket?.invalid_clips)
            ? basket.invalid_clips : [];
          fastFail = { fallback, invalid };
          break;
        }
        // Concat started or downstream pipeline took over — happy path.
        if (status && status !== "clips_staged" &&
            status !== "scanning_clips") {
          break;
        }
      }
      if (fastFail) {
        const headline = fastFail.invalid.length
          ? `${fastFail.invalid.length} clip(s) couldn't be analysed.`
          : "Analysis couldn't start with these clips.";
        const detailLines = fastFail.invalid
          .slice(0, 5)
          .map((c) => `• ${c.filename || c.clip_id || "clip"}: ${c.reason || "invalid video"}`)
          .join("\n");
        const full = detailLines
          ? `${headline}\n${detailLines}`
          : `${headline} (${fastFail.fallback})`;
        intakeLog("v2_analyse_failed", {
          status: 0,
          error: full,
          fallback_used: fastFail.fallback,
        });
        toast.error(headline);
        setAnalyseError(full);
        // DO NOT clearDraft — user can retry / remove the bad clip.
        return;
      }

      toast.success("Multi-clip lesson started — analysing now.");
      // Hand off FIRST so the navigation succeeds; only then clear
      // the draft. If onHandedOff throws we keep the draft so the
      // user can retry. P0 May 24, 2026 — operator brief said the
      // post-upload screen was collapsing; this ordering prevents
      // a fault on the receiving surface from leaving the user
      // with zero retry surface.
      try {
        onHandedOff?.(basketId);
      } finally {
        clearDraft(uid);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[v2] analyse failed", err);
      // Render a TRUTHFUL recoverable error. The backend returns
      // either a plain-string detail OR a structured object
      // `{message, incomplete_clips}` from the 409 path. The
      // previous toast.error(err.response.data.detail) rendered
      // the object as "[object Object]" — confusing the operator
      // and contributing to the "collapse" feel.
      const data = err?.response?.data;
      let userMessage = null;
      const det = data?.detail;
      if (typeof det === "string") {
        userMessage = det;
      } else if (det && typeof det === "object" && typeof det.message === "string") {
        userMessage = det.message;
        if (Array.isArray(det.incomplete_clips) && det.incomplete_clips.length) {
          const sample = det.incomplete_clips.slice(0, 3)
            .map((c) => c.filename || c.id || "clip")
            .join(", ");
          userMessage += ` (${sample}${det.incomplete_clips.length > 3 ? "…" : ""})`;
        }
      } else if (typeof data === "string") {
        userMessage = data;
      } else {
        userMessage = err?.message || "Couldn't start analysis.";
      }
      intakeLog("v2_analyse_failed", {
        status: err?.response?.status || null,
        error: userMessage,
      });
      toast.error(userMessage);
      // Surface a persistent, recoverable error in the basket UI so
      // the user has a visible retry surface (not just a fading toast).
      setAnalyseError(userMessage);
      // Intentionally DO NOT clearDraft here — the user can retry
      // after fixing whatever the truthful error told them.
    } finally {
      setVerifying(false);
    }
  }, [onHandedOff, uid, appendEvent]);

  const counts = {
    total: draft.rows.length,
    staged: draft.rows.filter((r) => r.status === "local_staged").length,
    uploading: draft.rows.filter((r) => r.status === "uploading").length,
    uploaded: draft.rows.filter((r) => r.status === "uploaded").length,
    failed: draft.rows.filter((r) => r.status === "failed" || r.status === "import_failed").length,
    needsRepick: draft.rows.filter((r) => !r.file && (r.status === "local_staged" || r.status === "failed")).length,
  };
  const canAnalyse = counts.total > 0 && counts.total === counts.uploaded;
  const anyUploading = counts.uploading > 0;

  // Media quarantine — silent. ClipBasketV2 is the V2 multi-clip
  // basket; under quarantine its endpoints all return 503. Hide
  // the whole surface so users land on the V3 canonical UploadShell
  // path instead (see MEDIA_PATH_TRUTH_AUDIT_V2 §1).
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <div data-testid="clip-basket-v2" role="dialog" aria-modal="true"
      className="fixed inset-0 z-[9000] bg-slate-950/95 backdrop-blur-sm overflow-y-auto">

      {/*
        MobilePhotosIntake is mounted unconditionally at the TOP of
        the modal — never behind the intent gate, never behind the
        row list, never re-keyed. This stable mount is the contract
        that makes the iOS Photos picker survivable.
      */}
      <MobilePhotosIntake
        ref={intakeRef}
        onFilesStaged={handleFilesStaged}
        onPickerStuck={handlePickerStuck}
        testid="clip-basket-v2-file-input"
      />

      <div className="max-w-md mx-auto min-h-full flex flex-col p-4">
        <header className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-amber-300">Upload basket v2</p>
            <h1 className="text-xl font-extrabold text-white">Multi-clip lesson</h1>
            {/* Visible upload-path debug badge (May 24, 2026 — Pass 1).
                Shows the operator-visible truth about which transport
                each clip used. `cloud` rows are R2-native (zero disk
                on preview). `relay` rows are the legacy chunked path
                (resumes of pre-Pass-1 sessions). */}
            {(() => {
              const rows = draft.rows || [];
              if (rows.length === 0) return null;
              const cloudCount = rows.filter((r) => r.uploadPath === "cloud").length;
              const relayCount = rows.filter((r) => r.uploadPath === "chunked").length;
              const path = cloudCount > 0 && relayCount === 0
                ? "R2-native (cloud)"
                : relayCount > 0 && cloudCount === 0
                ? "Local relay"
                : cloudCount > 0 || relayCount > 0
                ? "Mixed"
                : null;
              if (!path) return null;
              return (
                <p
                  data-testid="clip-basket-v2-upload-path-badge"
                  className="mt-1 text-[10px] font-mono text-cyan-300"
                >
                  Upload path: {path}
                </p>
              );
            })()}
          </div>
          <button type="button" data-testid="clip-basket-v2-close" onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-300" />
          </button>
        </header>

        {!draft.intent && (
          <section data-testid="clip-basket-v2-intent" className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
            <p className="text-[13px] font-extrabold text-white mb-2">What is this footage for?</p>
            <p className="text-[11px] text-slate-400 mb-3">Pick the right one — the AI grades differently for each.</p>
            <ul className="space-y-2">
              {INTENT_OPTIONS.map((opt) => (
                <li key={opt.id}>
                  <button type="button" data-testid={`clip-basket-v2-intent-option-${opt.id}`}
                    onClick={() => setDraft((d) => ({ ...d, intent: opt.id }))}
                    className="w-full text-left rounded-xl border border-slate-700 bg-slate-900 hover:bg-slate-800 px-3 py-2.5 text-[13px] text-slate-100">
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {draft.intent && (
          <section className="mb-4">
            <button type="button" data-testid="clip-basket-v2-add-videos" onClick={openPicker} disabled={anyUploading}
              className="w-full rounded-xl bg-amber-400 hover:bg-amber-300 disabled:bg-slate-700 disabled:text-slate-500 text-[#050505] font-extrabold py-3 flex items-center justify-center gap-2 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />Add videos from Photos
            </button>
            {pickerStuck && (
              <div data-testid="clip-basket-v2-picker-stuck"
                className="mt-3 rounded-xl border border-amber-400/40 bg-amber-500/10 p-3 text-[12px] text-amber-100 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-none mt-0.5 text-amber-300" />
                <div>
                  <p className="font-extrabold text-amber-50">We didn't receive your videos.</p>
                  <p className="text-amber-100/80 mt-0.5">Photos didn't return any files. Tap <b>Add videos from Photos</b> again — your existing rows are safe.</p>
                </div>
              </div>
            )}
          </section>
        )}

        {counts.total > 0 && (
          <section data-testid="clip-basket-v2-counters"
            data-total={counts.total} data-staged={counts.staged} data-uploading={counts.uploading}
            data-uploaded={counts.uploaded} data-failed={counts.failed} data-needs-repick={counts.needsRepick}
            className="rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2 mb-4 text-[11px] text-slate-300 flex flex-wrap gap-x-3 gap-y-1">
            <span data-testid="clip-basket-v2-count-total">Total <b className="text-white">{counts.total}</b></span>
            <span data-testid="clip-basket-v2-count-staged">Staged <b className="text-cyan-300">{counts.staged}</b></span>
            <span data-testid="clip-basket-v2-count-uploading">Uploading <b className="text-indigo-300">{counts.uploading}</b></span>
            <span data-testid="clip-basket-v2-count-uploaded">Uploaded <b className="text-emerald-300">{counts.uploaded}</b></span>
            <span data-testid="clip-basket-v2-count-failed">Failed <b className="text-rose-300">{counts.failed}</b></span>
            {counts.needsRepick > 0 && (
              <span data-testid="clip-basket-v2-count-needs-repick">Re-pick <b className="text-amber-300">{counts.needsRepick}</b></span>
            )}
          </section>
        )}

        {draft.rows.length > 0 && (
          <ul data-testid="clip-basket-v2-rows" className="space-y-2 mb-4">
            {draft.rows.map((r) => (
              <li key={r.rowId} data-testid="clip-basket-v2-row" data-row-id={r.rowId} data-row-status={r.status}
                className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 flex items-center gap-3">
                <div className="flex-none w-7 h-7 rounded-md bg-slate-900 border border-slate-700 flex items-center justify-center">
                  {r.status === "uploaded" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                   : (r.status === "failed" || r.status === "import_failed") ? <AlertCircle className="w-3.5 h-3.5 text-rose-300" />
                   : r.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 text-indigo-300 animate-spin" />
                   : <div className="w-2 h-2 rounded-full bg-cyan-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-bold text-white truncate">{r.fingerprint?.name || "(no name)"}</p>
                  <p className="text-[10.5px] text-slate-400 mt-0.5">
                    {r.status === "uploading" ? `Uploading… ${Math.round(r.progressPct || 0)}%`
                     : r.status === "uploaded" ? "Uploaded · ready to analyse"
                     : (r.status === "failed" || r.status === "import_failed") ? (r.error || "Failed")
                     : !r.file ? "Re-pick to upload (file lost after reload)"
                     : "Staged · waiting for Start"}
                  </p>
                  {r.warning && r.status === "local_staged" && (
                    <p data-testid="clip-basket-v2-row-warning"
                      className="text-[10.5px] text-amber-300 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {r.warning}
                    </p>
                  )}
                  {r.status === "uploading" && (
                    <div className="mt-1.5 h-1 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-indigo-400" style={{ width: `${r.progressPct || 0}%` }} />
                    </div>
                  )}
                </div>
                {r.status !== "uploading" && r.status !== "uploaded" && (
                  <button type="button" data-testid="clip-basket-v2-row-remove" onClick={() => removeRow(r.rowId)}
                    className="flex-none w-8 h-8 rounded-md hover:bg-slate-800 flex items-center justify-center" title="Remove">
                    <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                )}
                {r.status === "failed" && r.file && (
                  <button type="button" data-testid="clip-basket-v2-row-retry" onClick={() => uploadRow(r.rowId)}
                    className="flex-none px-2 h-8 rounded-md bg-rose-500/20 hover:bg-rose-500/30 text-[11px] font-extrabold text-rose-200">
                    Retry
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {draft.rows.length > 0 && (
          <div className="space-y-2 mt-auto">
            {analyseError && (
              <div
                data-testid="clip-basket-v2-analyse-error"
                className="rounded-xl border border-rose-500/50 bg-rose-950/40 px-3 py-2 text-[12px] text-rose-200"
                role="alert"
              >
                <div className="font-semibold mb-1">Analysis didn't start</div>
                <div className="text-rose-100/90 leading-snug">{analyseError}</div>
                <button
                  type="button"
                  data-testid="clip-basket-v2-retry-analyse"
                  onClick={analyseBasket}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-rose-500 hover:bg-rose-400 px-3 py-1.5 text-white text-[12px] font-bold"
                >
                  <Brain className="w-3.5 h-3.5" />
                  Retry analysis
                </button>
              </div>
            )}
            {counts.staged > 0 && (
              <button type="button" data-testid="clip-basket-v2-start-uploads" onClick={startAllUploads} disabled={anyUploading}
                className="w-full rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-extrabold py-3 flex items-center justify-center gap-2">
                {anyUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                              : <>Start uploads ({counts.staged})</>}
              </button>
            )}
            <button type="button" data-testid="clip-basket-v2-analyse" onClick={analyseBasket} disabled={!canAnalyse || verifying}
              className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-800 disabled:text-slate-600 text-white font-extrabold py-3 flex items-center justify-center gap-2">
              {verifying
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying clips…</>
                : <><Brain className="w-4 h-4" />
                    {canAnalyse ? `Analyse ${counts.uploaded} clip${counts.uploaded === 1 ? "" : "s"}` : "Analyse (waiting for uploads)"}
                  </>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}